import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, ExternalLink, ShieldCheck, AlertTriangle, Bell, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const KIND = {
  my_application: { label: "My application", dot: "bg-forest", chip: "bg-forest/10 text-forest border-forest/30" },
  draw_application: { label: "Draw application", dot: "bg-saddle", chip: "bg-saddle/12 text-saddle border-saddle/30" },
  raffle_purchase: { label: "Raffle / Super Tag", dot: "bg-blaze", chip: "bg-blaze/10 text-blaze-dark border-blaze/30" },
  special_application: { label: "Special permit", dot: "bg-[#B7761F]", chip: "bg-[#B7761F]/10 text-[#B7761F] border-[#B7761F]/30" },
  drawing: { label: "Raffle drawing", dot: "bg-charcoal", chip: "bg-charcoal/8 text-charcoal border-charcoal/25" },
  results: { label: "Results expected", dot: "bg-[#2E7D46]", chip: "bg-[#2E7D46]/10 text-[#2E7D46] border-[#2E7D46]/30" },
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function fmtDate(s) {
  try { return new Date(s + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }); }
  catch { return s; }
}

function DaysChip({ item }) {
  if (item.days_until == null) return null;
  if (item.status === "past") return <span className="text-[11px] font-semibold text-charcoal/40">{Math.abs(item.days_until)}d ago</span>;
  if (item.days_until === 0) return <span className="text-[11px] font-bold text-blaze">Today</span>;
  return <span className={`text-[11px] font-bold ${item.days_until <= 21 ? "text-blaze" : "text-forest"}`}>in {item.days_until}d</span>;
}

function DeadlineRow({ item }) {
  const k = KIND[item.kind] || KIND.special_application;
  return (
    <div className="bg-white border border-saddle/20 p-3.5 flex flex-col gap-1.5" data-testid={`dl-item-${item.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border ${k.chip}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${k.dot}`} /> {k.label}{item.type_label && item.kind !== "my_application" ? ` · ${item.type_label}` : ""}
        </span>
        <DaysChip item={item} />
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-serif text-lg text-forest leading-snug">{item.title}</p>
        <p className="text-xs text-charcoal/50 tabular-nums shrink-0">{fmtDate(item.date)}</p>
      </div>
      {(item.state || item.species) && <p className="text-[11px] text-charcoal/50">{[item.state, item.species && item.species !== "Multiple" ? item.species : null].filter(Boolean).join(" · ")}</p>}
      {item.detail && <p className="text-xs text-charcoal/60">{item.detail}</p>}
      <div className="flex items-center gap-3 mt-0.5">
        {item.source && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-charcoal/45">
            {item.confidence === "verified" ? <ShieldCheck size={11} className="text-[#2E7D46]" /> : item.confidence === "needs_review" ? <AlertTriangle size={11} className="text-[#B7761F]" /> : null}
            {item.source}{item.confidence === "needs_review" ? " · needs review" : ""}
          </span>
        )}
        {item.official_url && <a href={item.official_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blaze hover:underline" data-testid={`dl-official-${item.id}`}>Official page <ExternalLink size={11} /></a>}
        {item.link && <Link to={item.link} className="text-xs font-semibold text-forest hover:underline" data-testid={`dl-open-${item.id}`}>{item.kind === "my_application" ? "Open tracker" : "View in Planner"}</Link>}
      </div>
    </div>
  );
}

const REMINDER_TYPES = [
  ["applications", "My tracked applications"],
  ["draws", "Draw application deadlines"],
  ["special", "Raffles & special draws"],
  ["results", "Drawings & results"],
];

function ReminderSettings() {
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState(null);
  const [choices, setChoices] = useState([7, 14, 21, 30]);
  useEffect(() => {
    if (open && leads === null) {
      api.getDeadlineReminders().then((d) => { setLeads(d.leads); setChoices(d.choices); }).catch(() => setLeads({}));
    }
  }, [open]);
  const save = async (k, v) => {
    const next = { ...leads, [k]: Number(v) };
    setLeads(next);
    try { const r = await api.putDeadlineReminders({ leads: next }); setLeads(r.leads); toast.success("Reminder timing saved"); }
    catch { toast.error("Couldn't save reminder timing"); }
  };
  return (
    <div className="relative" data-testid="dl-reminder-settings">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest border border-saddle/25 px-3 py-2 hover:border-forest/50 bg-white" data-testid="dl-reminder-toggle">
        <Settings2 size={15} /> Reminder timing
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-saddle/25 shadow-xl p-4 z-40">
          <p className="hr-overline text-charcoal/50 mb-3">Remind me this far ahead</p>
          {leads === null ? <Loader2 className="animate-spin text-blaze mx-auto" size={18} /> : (
            <div className="space-y-2.5">
              {REMINDER_TYPES.map(([k, label]) => (
                <label key={k} className="flex items-center justify-between gap-3 text-sm text-charcoal/75">
                  <span>{label}</span>
                  <select value={leads[k] ?? 14} onChange={(e) => save(k, e.target.value)} className="border border-saddle/25 px-2 py-1 text-sm bg-white" data-testid={`dl-remind-${k}`}>
                    {choices.map((c) => <option key={c} value={c}>{c} days</option>)}
                  </select>
                </label>
              ))}
              <p className="text-[11px] text-charcoal/45 pt-1">Bell notifications fire this many days before each deadline.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Deadlines() {
  const { isAuthed, ready } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(iso(today));

  useEffect(() => {
    if (!isAuthed) return;
    api.myDeadlines().then(setData).catch(() => setData({ deadlines: [], followed_states: [] }));
  }, [isAuthed]);

  const byDate = useMemo(() => {
    const m = {};
    for (const d of data?.deadlines || []) (m[d.date] = m[d.date] || []).push(d);
    return m;
  }, [data]);

  if (!ready || (isAuthed && data === null)) {
    return <div className="min-h-screen grid place-items-center bg-paper"><Loader2 className="animate-spin text-blaze" /></div>;
  }
  if (!isAuthed) {
    return (
      <div className="bg-paper min-h-screen flex flex-col"><SiteHeader />
        <div className="flex-1 grid place-items-center px-6">
          <div className="text-center max-w-md">
            <CalendarDays size={36} className="text-blaze mx-auto" />
            <h1 className="font-serif text-4xl text-charcoal mt-4 tracking-tight">Your deadline calendar</h1>
            <p className="text-charcoal/60 mt-2">Sign in to see every application, raffle and drawing deadline across the states you follow.</p>
            <button onClick={() => navigate("/auth", { state: { from: "/deadlines" } })} className="mt-6 bg-blaze text-white px-8 py-3 font-semibold uppercase tracking-wide text-sm hover:brightness-95" data-testid="deadlines-signin">Sign in</button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(iso(new Date(year, month, d)));
  while (cells.length % 7) cells.push(null);

  const upcoming = (data.deadlines || []).filter((d) => d.status === "open").slice(0, 12);
  const followed = data.followed_states || [];

  return (
    <div className="bg-paper min-h-screen text-charcoal" data-testid="deadlines-page">
      <SiteHeader />
      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-forest"><Bell size={18} className="text-blaze" />
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Deadline calendar</h1>
            </div>
            <p className="text-sm text-charcoal/55 mt-2 max-w-2xl">
              Every upcoming application, raffle and drawing deadline{followed.length ? ` across ${followed.join(", ")}` : ""} — plus results dates and deadlines you're tracking yourself.
            </p>
          </div>
          <ReminderSettings />
        </div>

        {followed.length === 0 && (
          <div className="mt-5 border border-dashed border-saddle/40 bg-white p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="dl-follow-banner">
            <p className="text-sm text-charcoal/65">You're not following any states yet — follow states to see their raffle and special-draw deadlines here automatically.</p>
            <Link to="/planner" className="text-sm font-semibold text-blaze hover:underline">Open Planner</Link>
          </div>
        )}

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-6">
          <div className="bg-white border border-saddle/20 p-4">
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 border border-saddle/20 hover:border-forest/40" aria-label="Previous month" data-testid="dl-prev-month"><ChevronLeft size={16} /></button>
              <h2 className="font-serif text-xl text-forest" data-testid="dl-month-label">{cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</h2>
              <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 border border-saddle/20 hover:border-forest/40" aria-label="Next month" data-testid="dl-next-month"><ChevronRight size={16} /></button>
            </div>
            <div className="grid grid-cols-7 text-center text-[10px] uppercase tracking-wide text-charcoal/40 mb-1">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d} className="py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-px bg-saddle/15 border border-saddle/15">
              {cells.map((dIso, i) => {
                const dayItems = dIso ? byDate[dIso] || [] : [];
                const isToday = dIso === iso(today);
                const isSel = dIso === selected;
                return dIso ? (
                  <button key={dIso} onClick={() => setSelected(dIso)}
                    className={`bg-white min-h-[64px] p-1.5 text-left align-top transition-colors ${isSel ? "outline outline-2 outline-blaze -outline-offset-2" : "hover:bg-bone"}`}
                    data-testid={`dl-day-${dIso}`}>
                    <span className={`text-xs tabular-nums ${isToday ? "inline-grid place-items-center w-5 h-5 rounded-full bg-forest text-bone font-bold" : "text-charcoal/60"}`}>{Number(dIso.slice(-2))}</span>
                    <span className="flex flex-wrap gap-0.5 mt-1">
                      {dayItems.slice(0, 4).map((it) => <span key={it.id} className={`w-2 h-2 rounded-full ${(KIND[it.kind] || KIND.special_application).dot}`} title={it.title} />)}
                      {dayItems.length > 4 && <span className="text-[9px] text-charcoal/45">+{dayItems.length - 4}</span>}
                    </span>
                  </button>
                ) : <div key={`pad-${i}`} className="bg-paper/60 min-h-[64px]" />;
              })}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3" data-testid="dl-legend">
              {Object.values(KIND).map((k) => (
                <span key={k.label} className="inline-flex items-center gap-1.5 text-[11px] text-charcoal/55"><span className={`w-2 h-2 rounded-full ${k.dot}`} /> {k.label}</span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="hr-overline text-charcoal/50 mb-2" data-testid="dl-selected-label">{fmtDate(selected)}</h3>
            <div className="space-y-2.5" data-testid="dl-day-items">
              {(byDate[selected] || []).length === 0
                ? <p className="text-sm text-charcoal/45 border border-dashed border-saddle/25 p-4" data-testid="dl-day-empty">No deadlines on this day.</p>
                : (byDate[selected] || []).map((it) => <DeadlineRow key={it.id} item={it} />)}
            </div>
          </div>
        </div>

        <h2 className="font-serif text-2xl text-forest mt-10 tracking-tight">Upcoming</h2>        {upcoming.length === 0 ? (
          <p className="text-sm text-charcoal/50 mt-2 border border-dashed border-saddle/25 p-4" data-testid="dl-upcoming-empty">
            Nothing upcoming right now. Follow states and track applications to fill your calendar — new deadlines appear as they're verified.
          </p>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3" data-testid="dl-upcoming">
            {upcoming.map((it) => <DeadlineRow key={it.id} item={it} />)}
          </div>
        )}
        {(data.windows || []).length > 0 && (
          <div className="mt-8" data-testid="dl-windows">
            <h2 className="font-serif text-xl text-forest tracking-tight">Results windows — exact dates not yet announced</h2>
            <p className="text-xs text-charcoal/50 mt-1 mb-3">These agencies publish a window rather than an exact date, so they don't get a calendar pin. Watch the official page.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {data.windows.map((w, i) => (
                <div key={i} className="bg-white border border-dashed border-saddle/30 p-3" data-testid={`dl-window-${i}`}>
                  <p className="text-sm font-semibold text-charcoal/80">{w.state} {w.species}{w.residency && w.residency !== "Either" ? ` (${w.residency.toLowerCase()})` : ""}</p>
                  <p className="text-xs text-charcoal/55 mt-0.5">{w.window}</p>
                  {w.source_url && <a href={w.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-blaze hover:underline mt-1">Official page <ExternalLink size={10} /></a>}
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-[11px] text-charcoal/45 mt-4 flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-[#2E7D46]" /> Only verified official deadlines are listed — programs whose dates aren't confirmed yet are marked on their cards instead of guessed here.
        </p>
      </main>
      <Footer />
    </div>
  );
}
